import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { Button, TextBox } from "../../../../components";
import { Form, Formik } from "formik";
import { formatNumber } from "../../../../helpers/common.helper";
import TextArea from "../../../../components/common/TextArea";
import { ReactComponent as TrashSvg } from "../../../../assets/admin/icons/trash.svg";
import { ReactComponent as EmptySvg } from "../../../../assets/icons/empty-projects.svg";
import _, { ceil } from "lodash";
import { useNavigate, useParams } from "react-router-dom";
import QuotationTenderingContext from "../QuotationTenderingContext";

const Item = forwardRef(
  ({ name, price, areaSize, areaUnit, remark, onChange, onDelete }, ref) => {
    const [hasErrors, setHasErrors] = useState(true);
    const formikRef = useRef();

    useImperativeHandle(ref, () => ({
      hasErrors,
    }));

    useEffect(() => {
      const values = formikRef.current.values;
      onChange(values);
    }, [hasErrors]);

    const handleValidate = ({ name, price, areaSize, areaUnit, remark }) => {
      let errors = {};

      if (!name) errors.name = "Please specify item name";
      if (!price) errors.price = "Please specify a price";
      if (!areaSize) errors.areaSize = "Unit is required";

      if (!_.isEmpty(errors)) {
        setHasErrors(true);
        return errors;
      }

      setHasErrors(false);
      onChange({ name, price, areaSize, areaUnit, remark });
      return errors;
    };

    return (
      <Formik
        innerRef={formikRef}
        initialValues={{ name, price, areaSize, areaUnit, remark }}
        validate={handleValidate}
      >
        {({ values }) => {
          return (
            <Form className="tendering-quotation__design-detail__item">
              <div
                style={{
                  gridArea: "name",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                  width: "100%",
                }}
              >
                <TextBox
                  name="name"
                  label="Item name"
                  style={{ flexGrow: 1 }}
                />
                <div
                  className="tendering-quotation__design-detail__item__delete"
                  onClick={() => onDelete()}
                >
                  <TrashSvg height={18} width={18} />
                </div>
              </div>
              <TextBox
                name="price"
                label="Price per unit"
                style={{ gridArea: "price" }}
                type="number"
              />
              <TextBox
                name="areaSize"
                label="Area & Unit"
                style={{ gridArea: "unit" }}
                type="number"
              />
              <div className="tendering-quotation__design-detail__item__total">
                <span className="tendering-quotation__design-detail__item__total__label">
                  Total
                </span>
                <span className="tendering-quotation__design-detail__item__total__value">
                  {formatNumber(values.price * values.areaSize, "THB")}
                </span>
              </div>
              <TextArea
                name="remark"
                label="Remark"
                style={{ gridArea: "remark" }}
              />
            </Form>
          );
        }}
      </Formik>
    );
  }
);

const Detail = (props) => {
  const { setStep, details, setDetails, goForward, goBack } = useContext(
    QuotationTenderingContext
  );
  const [grandTotal, setGrandTotal] = useState(0);
  const [cannotContinue, setCannotContinue] = useState(false);
  const itemRefs = useRef([]);
  // FIXME: Remove test data
  const [items, setItems] = useState(
    details?.items ?? [
      {
        name: "Test",
        price: 1000,
        areaSize: 100,
        areaUnit: "sq.m.",
        remark: "- Interior Construction Drawing",
      },
    ]
  );

  useEffect(() => {
    setStep(1);
  }, []);

  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, items.length);
    setGrandTotal(_getGrandTotal());
  }, [items]);

  const _getGrandTotal = () => {
    const grandTotal = items.reduce((prev, curr) => {
      return prev + +(curr.price ?? 0) * +(curr.areaSize ?? 0);
    }, 0);
    return grandTotal;
  };

  const _handleItemChange = () => {
    setGrandTotal(_getGrandTotal());

    setCannotContinue(
      itemRefs.current.find((i) => i.hasErrors === true) !== undefined
    );
  };

  return (
    <>
      <div className="tendering-quotation__header">
        <h3>Detail</h3>
        <Button
          label={"Add item"}
          size={"small"}
          onClick={() => {
            setItems((curr) =>
              curr.concat({
                name: "",
                price: 0,
                areaSize: 0,
                areaUnit: "sq.m.",
                remark: "",
              })
            );

            setCannotContinue(true);
          }}
        />
      </div>
      <div className="tendering-quotation__content">
        <div className="tendering-quotation__design-detail">
          {items.map((item, index) => (
            <>
              <Item
                ref={(el) => (itemRefs.current[index] = el)}
                {...item}
                onDelete={() => {
                  setItems((curr) => {
                    return curr.filter((_, i) => index !== i);
                  });

                  _handleItemChange();
                }}
                onChange={(data) => {
                  setItems((curr) => {
                    curr[index] = data;
                    return curr;
                  });

                  _handleItemChange();
                }}
              />
              {index < items.length - 1 && (
                <div className="tendering-quotation__design-detail__separator" />
              )}
            </>
          ))}
          {items.length > 0 && (
            <div className="tendering-quotation__design-detail__grand-total">
              Grand Total
              <div className="tendering-quotation__design-detail__grand-total__value">
                {formatNumber(grandTotal, "THB")}
              </div>
            </div>
          )}
          {items.length === 0 && (
            <div className="tendering-quotation__design-detail__empty">
              <EmptySvg style={{ opacity: 0.2 }} width={256} />
              <span>No items found, please add at least 1 item</span>
            </div>
          )}
        </div>
      </div>
      <div className="tendering-quotation__footer">
        <Button label="Return" variant={"secondary"} onClick={goBack} />
        <Button
          label="Continue"
          disabled={items.length === 0 || cannotContinue}
          onClick={() => {
            setDetails((curr) => ({ ...curr, items }));
            goForward();
          }}
        />
      </div>
    </>
  );
};

Detail.propTypes = {};

export default Detail;
